<script lang="ts" setup>
defineProps({
  feature: {
    default: undefined,
    type: String as PropType<SegmentationFeature>,
  },
  list: {
    default: undefined,
    type: Object as PropType<List>,
  },
  selection: {
    default: undefined,
    type: Array as PropType<number[]>,
  },
  togglingFromList: {
    default: undefined,
    type: Boolean,
  },
})

const emit = defineEmits<{
  removeSelectedCompaniesFromList: []
  toggleCompaniesFromList: [{ action: 'attach' | 'detach', companies: CompanyV2[], listId: number }]
}>()

const { t } = useI18n()

const isListsSelectorOpen = ref(false)
const attachListSelectorRef = ref<ComponentPublicInstance>()

function onCloseListsSelector() {
  useRafTimeout(
    () => (isListsSelectorOpen.value = false),
    100,
    { immediate: true },
  )
}

function onToggleListsSelector() {
  if (isListsSelectorOpen.value) {
    return
  }

  attachListSelectorRef.value?.$el?.nextSibling?.querySelector('[role="button"]')?.click()
  isListsSelectorOpen.value = true
}
</script>

<template>
  <div class="flex max-w-full flex-1 items-center gap-2 self-end whitespace-nowrap">
    <template v-if="selection?.length">
      <UTooltip
        v-if="list"
        :popper="{ placement: 'bottom' }"
        :text="t('removeSelectedCompaniesFromList', selection.length)"
      >
        <UButton
          color="pink"
          :icon="getIcon('trash')"
          :loading="togglingFromList"
          size="md"
          variant="subtle"
          @click="emit('removeSelectedCompaniesFromList')"
        />
      </UTooltip>

      <div
        v-if="feature && ['search', 'similar'].includes(feature) && !list"
        class="group relative"
      >
        <UTooltip
          class="z-30"
          :popper="{ placement: 'bottom', adaptive: true }"
          :text="t('attachSelectedCompaniesToLists', { count: selection.length })"
        >
          <UButton
            class="z-20 group-hover:ring-opacity-50 md:flex"
            :icon="getIcon('save')"
            :loading="togglingFromList"
            size="md"
            variant="subtle"
            @click="onToggleListsSelector"
          />
        </UTooltip>

        <CompanyListsSelector
          v-if="['search', 'similar'].includes(feature)"
          ref="attachListSelectorRef"
          class="absolute left-0 top-0 z-20 size-full md:flex md:flex-initial"
          :companies="selection.map(selection => ({ meta: { mysqlId: selection } }))"
          size="md"
          :ui="{ base: 'w-0 !pe-0 !ps-0 !cursor-pointer', icon: { base: 'hidden' } }"
          :ui-menu="{ container: 'min-w-64' }"
          @close="onCloseListsSelector"
          @toggle-companies="emit('toggleCompaniesFromList', $event)"
        />
      </div>
    </template>

    <div class="flex gap-2 overflow-x-auto whitespace-nowrap">
      <UButton
        v-if="selection?.length"
        class="!gradient-texture-reverse flex items-center justify-center !bg-transparent md:min-w-32"
        size="sm"
      >
        <div class="flex items-center gap-2">
          <UIcon
            class="size-5 flex-shrink-0"
            :name="getIcon('check')"
          />

          <span class="text-sm uppercase">
            <span class="text-sm uppercase">{{ numbro(selection.length) }} {{ t('globals.selected') }}</span>
          </span>
        </div>
      </UButton>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "attachSelectedCompaniesToLists": "Save selected company to a list | Save {count} selected companies to a list",
    "removeSelectedCompaniesFromList": "Remove selected company from this list | Remove selected companies from this list"
  },
  "es": {
    "attachSelectedCompaniesToLists": "Guardar la empresa seleccionada en una lista | Guardar {count} empresas seleccionadas en una lista",
    "removeSelectedCompaniesFromList": "Eliminar la empresa seleccionada de esta lista | Eliminar las empresas seleccionadas de esta lista"
  },
  "fr": {
    "attachSelectedCompaniesToLists": "Attacher l'entreprise sélectionnée à une liste | Attacher {count} entreprises sélectionnées à une liste",
    "removeSelectedCompaniesFromList": "Supprimer l'entreprise sélectionnée de cette liste | Supprimer les entreprises sélectionnées de cette liste"
  }
}
</i18n>
